"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IExtendedSeat,
  ISection,
  SeatmapBookingRenderer,
} from "@seatmap.pro/renderer";
import { useTranslations } from "~/hooks/useTranslations";
import { PlainMessage } from "@bufbuild/protobuf";
import {
  EventSeriesDetails,
  Price,
  Section,
} from "@egocentric-systems/ts-apis/booking_gateway/v1/events_pb";
import { SelectPrice } from "./select-price";
import { useCart } from "~/hooks/useCart";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSeatmapUpdates } from "~/hooks/useSeatmapUpdates";
import { useMediaQuery } from "usehooks-ts";
import { cn, moneyToCurrency } from "~/lib/utils";
import { TicketControls } from "~/components/TicketControls";
import {
  getMaxPriceValue,
  isPriceWithinRange,
  sortPricesByValue,
} from "~/app/tickets/[eventId]/_components/helpers";
import { usePricesSearchParams } from "~/app/tickets/[eventId]/_components/Filter/hooks";
import { EventOrBundle, UNLOCK_CODE_COOKIE } from "~/lib/definitions";
import showdown from "showdown";
import { SeatUpgrade } from "@egocentric-systems/ts-apis/booking/types/v1/order_pb";
import { useNewItemIsInstantlyUpgraded } from "~/hooks/useNewItemIsInstantlyUpgraded";
import { UnlockBadge } from "~/app/tickets/[eventId]/_components/UnlockBadge";
import cookie from "cookiejs";
import { Triangle } from "lucide-react";
import { SeatInfoTooltip } from "./SeatInfoTooltip";

interface Props {
  seriesDetails: PlainMessage<EventSeriesDetails>;
  eventOrBundle: EventOrBundle;
  sections: Array<PlainMessage<Section>>;
  sectionMapping: Record<string | number, string[]>;
  buttonBackground?: string;
}

export function SeatmapRenderer({
  seriesDetails,
  eventOrBundle,
  sections,
  sectionMapping,
  buttonBackground,
}: Props) {
  const t = useTranslations();
  const isInstantlyUpgraded = useNewItemIsInstantlyUpgraded();

  const [{ min, max, category }] = usePricesSearchParams();

  const [selectedSeat, setSelectedSeat] = useState<IExtendedSeat | null>(null);
  const [selectedSection, setSelectedSection] = useState<ISection | null>(null);

  const [showSelectPrice, setShowSelectPrice] = useState(false);
  const [showDeselectSeat, setShowDeselectSeat] = useState(false);

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const [seatmapLoaded, setSeatmapLoaded] = useState(false);

  const seatmapRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<SeatmapBookingRenderer | null>(null);

  const onSelectSeat = useRef<((seat: IExtendedSeat) => void) | null>(null);

  const { items, uiItems, reserveSeat, cancelSeat } = useCart();

  const SEATMAP_HEIGHT = isDesktop ? 800 : 400;

  const [hoveredSeat, setHoveredSeat] = useState<IExtendedSeat | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const cartItemsWitSeatId = useMemo(
    () =>
      uiItems
        .filter(
          (item) =>
            item.eventOrBundle.case === eventOrBundle.case &&
            item.eventOrBundle.value.id === eventOrBundle.value?.id &&
            item.seat !== undefined,
        )
        .map((item) => item.seat?.id) as number[],
    [uiItems, eventOrBundle],
  );

  const { availableSeats, isSectionGaAndBookedOut } = useSeatmapUpdates(
    eventOrBundle,
    sectionMapping,
    { enabled: seatmapLoaded },
  );

  const [redrawed, setRedrawed] = useState(false);

  useEffect(() => {
    const unlockCode = cookie.get(UNLOCK_CODE_COOKIE);

    if (seatmapRef.current) {
      rendererRef.current = new SeatmapBookingRenderer(seatmapRef.current, {
        publicKey: "42",
        height: SEATMAP_HEIGHT,
        disableZoomToEmptySpace: true,
        baseUrl:
          eventOrBundle.case === "event"
            ? `${process.env.NEXT_PUBLIC_SEATMAPPRO_BOOKING_RENDERER_URL}/${seriesDetails.orgId}`
            : `${process.env.NEXT_PUBLIC_SEATMAPPRO_BOOKING_RENDERER_URL_BUNDLES}/${seriesDetails.orgId}`,
        onSeatSelect(seat) {
          onSelectSeat.current?.(seat);

          return false;
        },
        onSectionClick(seatmapSection) {
          const section = sections.find(
            (s) => s.seatmapSectionId === seatmapSection.id,
          );

          if (!(section?.prices as PlainMessage<Price>[] | undefined)?.length) {
            return;
          }

          setSelectedSeat(null);
          setSelectedSection(seatmapSection);
          setShowSelectPrice(true);
          setShowDeselectSeat(false);
        },
        onSeatMouseEnter(seat) {
          setShowTooltip(true);
          setHoveredSeat(seat);
        },
        onSeatMouseLeave() {
          setShowTooltip(false);
        },
        onBeforeSeatDraw({ seat, style }) {
          const customSeat = sections
            .flatMap(({ customSeats }) => customSeats)
            .find(
              (customSeats) =>
                customSeats?.seatIds.includes(seat.id) &&
                customSeats?.prices?.length &&
                seat.priceId,
            );

          const section = sections.find(
            ({ seatmapSectionId }) => seat.sectorId === seatmapSectionId,
          );

          const isUnlocked =
            customSeat?.prices?.some((price) => price.isUnlocked) ||
            section?.prices?.some((price) => price.isUnlocked);

          const priceGroupColor =
            customSeat?.priceGroupColor ?? section?.priceGroupColor ?? "";

          const seatColor =
            isUnlocked || !unlockCode
              ? priceGroupColor
              : `${priceGroupColor.substring(0, 7)}30`;

          const color = seat.id !== -1 ? seatColor : "transparent";

          return {
            ...style,
            color: seat.locked ? "#222" : color,
          };
        },
        onRedrawStart() {
          const seatmapSections = rendererRef.current?.getSections() ?? [];

          for (const seatmapSection of seatmapSections) {
            const elements =
              seatmapRef.current?.querySelectorAll(
                `g[data-gos-id='${seatmapSection.id}'] rect, g[data-gos-id='${seatmapSection.id}'] circle`,
              ) ?? [];

            const mappedSection = sections.find(
              (s) => s.seatmapSectionId === seatmapSection.id,
            );

            if (mappedSection?.prices?.length) {
              const isUnlocked = mappedSection.prices?.some(
                (price) => price.isUnlocked,
              );

              const priceGroupColor = mappedSection.priceGroupColor;

              const gaColor =
                isUnlocked || !unlockCode
                  ? priceGroupColor
                  : `${priceGroupColor}30`;

              for (const element of elements) {
                const htmlElement = element as HTMLElement;

                setTimeout(() => {
                  htmlElement.style.opacity = "0.5";

                  htmlElement.style.fill = gaColor;

                  htmlElement.style.pointerEvents = "";
                });
              }
            } else {
              for (const element of elements) {
                const htmlElement = element as HTMLElement;

                setTimeout(() => {
                  htmlElement.style.pointerEvents = "none";
                });
              }
            }
          }

          setRedrawed(true);
        },
      });

      rendererRef.current["createStatsSeatRequest"] = null;

      if (eventOrBundle.value?.id) {
        rendererRef.current
          .loadEvent(eventOrBundle.value.id)
          .then(() => setSeatmapLoaded(true));
      }
    }

    return () => {
      rendererRef.current?.destroy();
    };
  }, [eventOrBundle, seriesDetails.orgId, SEATMAP_HEIGHT, sections]);

  useEffect(() => {
    if (!redrawed) {
      return;
    }

    for (const s of sections) {
      const elements =
        seatmapRef.current?.querySelectorAll(
          `path[data-gos-id='${s.seatmapSectionId}']`,
        ) ?? [];

      const isBookedOut =
        availableSeats[s.id]?.length === 0 || !s.prices?.length;

      for (const element of elements) {
        const htmlElement = element as HTMLElement;

        htmlElement.style.pointerEvents = isBookedOut ? "none" : "";
      }
    }

    setRedrawed(false);
  }, [isSectionGaAndBookedOut, sections, redrawed, availableSeats]);

  useEffect(() => {
    const elements =
      seatmapRef.current?.querySelectorAll(
        "g[data-gos-id] rect, g[data-gos-id] circle",
      ) ?? [];

    for (const element of elements) {
      const seatmapSectionId =
        element.parentElement?.getAttribute("data-gos-id");

      const mappedSection = sections.find(
        (s) =>
          seatmapSectionId && s.seatmapSectionId === Number(seatmapSectionId),
      );

      const maxPriceAsNumber = getMaxPriceValue(mappedSection?.prices);

      (element as HTMLElement).style.opacity =
        isPriceWithinRange(maxPriceAsNumber, min, max) &&
        (!category || category === mappedSection?.priceGroupId)
          ? "0.5"
          : "0";
    }
  }, [sections, min, max, category]);

  useEffect(() => {
    try {
      rendererRef.current?.setSeatSelection(cartItemsWitSeatId);

      rendererRef.current?.updateSeatLocks((seat) => {
        const section = sections.find(
          (section) => section.seatmapSectionId === seat.sectorId,
        );

        const customSeats = sections
          .flatMap(({ customSeats }) => customSeats)
          .find(
            (customSeats) =>
              customSeats?.seatIds.includes(seat.id) && seat.priceId,
          );

        const sortedPrices = sortPricesByValue(
          customSeats?.prices ?? section?.prices,
        );
        const maxPriceAsNumber = getMaxPriceValue(sortedPrices);

        const priceGroupId = customSeats?.priceGroupId ?? section?.priceGroupId;

        return (
          (!cartItemsWitSeatId.includes(seat.id) &&
            !Object.values(availableSeats).every((availableSeatsPerEvent) =>
              availableSeatsPerEvent.includes(seat.id),
            )) ||
          !isPriceWithinRange(maxPriceAsNumber, min, max) ||
          (category && category !== priceGroupId) ||
          seat.priceId === undefined ||
          (customSeats === undefined && !section?.prices?.length) || // if there is an unlock code
          (customSeats !== undefined && !customSeats.prices?.length)
        );
      });
    } catch {
      // since loading the seatmap is asynchronous, it may happen that
      // the seats are still undefined when calling `setSeatSelection`
    }

    onSelectSeat.current = (seat: IExtendedSeat) => {
      const seatId = seat.id;

      if (
        seatId &&
        !cartItemsWitSeatId.includes(seatId) &&
        Object.values(availableSeats).every((availableSeatsPerEvent) =>
          availableSeatsPerEvent.includes(seatId),
        )
      ) {
        setSelectedSeat(seat);
        setSelectedSection(null);
        setShowSelectPrice(true);
        setShowDeselectSeat(false);
      } else if (seat.id && cartItemsWitSeatId.includes(seat.id)) {
        setSelectedSeat(seat);
        setSelectedSection(null);
        setShowSelectPrice(false);
        setShowDeselectSeat(true);
      }
    };
  }, [cartItemsWitSeatId, availableSeats, min, max, sections, category]);

  const section = sections.find(
    (section) =>
      section.seatmapSectionId ===
      (selectedSeat?.sectionId ?? selectedSection?.id),
  );

  const htmlDescription = useMemo(() => {
    return new showdown.Converter().makeHtml(section?.description ?? "");
  }, [section?.description]);

  const customSeat =
    section?.customSeats?.find((seat) =>
      seat.seatIds.includes(selectedSeat?.id ?? -1),
    ) ?? null;

  return (
    <>
      <section className="relative w-full">
        <div
          ref={seatmapRef}
          className="seatmap-renderer rounded-md !bg-gray-100"
        />

        <div
          style={{
            left: hoveredSeat?.x,
            top: (hoveredSeat?.y ?? 0) + 28,
          }}
          className={cn(
            "pointer-events-none absolute flex -translate-x-1/2 flex-col text-nowrap rounded-lg bg-black px-8 py-2 text-center text-sm text-white opacity-0 transition-opacity",
            showTooltip && "opacity-100",
          )}
        >
          <div className="absolute left-1/2 top-1 -translate-x-1/2 -translate-y-full">
            <Triangle className="fill-black stroke-none" />
          </div>

          {Object.values(availableSeats).every((availableSeatsPerEvent) =>
            availableSeatsPerEvent.includes(hoveredSeat?.id ?? Infinity),
          ) ? (
            <SeatInfoTooltip
              seat={hoveredSeat}
              section={sections.find(
                (s) => s.seatmapSectionId === hoveredSeat?.sectionId,
              )}
            />
          ) : (
            <span>{t.select_price.seat_not_available}</span>
          )}
        </div>

        {isDesktop && (
          <div className="absolute right-4 top-1/2 flex -translate-y-1/2 flex-col gap-2">
            <Button
              variant="outline"
              className="size-[48px] rounded-full"
              style={{ backgroundColor: buttonBackground }}
              onClick={() => rendererRef.current?.zoomIn()}
            >
              <FontAwesomeIcon icon={faPlus} />

              <span className="sr-only">{t.zoom_in}</span>
            </Button>

            <Button
              variant="outline"
              className="size-[48px] rounded-full"
              style={{ backgroundColor: buttonBackground }}
              onClick={() => rendererRef.current?.zoomOut()}
            >
              <FontAwesomeIcon icon={faMinus} />

              <span className="sr-only">{t.zoom_out}</span>
            </Button>
          </div>
        )}
      </section>

      <Dialog open={showSelectPrice} onOpenChange={setShowSelectPrice}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.select_price.select}</DialogTitle>
            <DialogDescription className="flex flex-col" asChild>
              <div>
                <span>
                  {selectedSeat
                    ? `${section?.name}, ${t.select_price.row} ${selectedSeat?.rowNumber},
                ${t.select_price.seat} ${selectedSeat?.seatName} ${
                  selectedSeat?.isAccessible ? t.select_price.accessible : ""
                }`
                    : section?.name}
                </span>
                <div
                  className="text-black [&_ul]:list-['-_'] [&_ul]:pl-3"
                  dangerouslySetInnerHTML={{ __html: htmlDescription }}
                />
              </div>
            </DialogDescription>
          </DialogHeader>

          {isSectionGaAndBookedOut(section?.id || section?.seatmapSectionId) ? (
            <span className="text-lg">{t.select_price.section_booked_out}</span>
          ) : selectedSeat ? (
            <SelectPrice
              prices={customSeat?.prices ?? section?.prices}
              onSelect={(price) => {
                if (seriesDetails.series && eventOrBundle.value && section) {
                  reserveSeat({
                    series: seriesDetails.series,
                    eventOrBundle:
                      eventOrBundle.case === "event"
                        ? {
                            case: "event",
                            value: eventOrBundle.value,
                          }
                        : {
                            case: "bundle",
                            value: eventOrBundle.value,
                          },
                    price,
                    section,
                    seat:
                      selectedSeat.id !== undefined
                        ? {
                            id: selectedSeat.id,
                            row: String(selectedSeat.rowNumber),
                            name: selectedSeat.seatName,
                          }
                        : undefined,
                    upgrades: isInstantlyUpgraded(eventOrBundle)
                      ? [SeatUpgrade.FANTICKET]
                      : undefined,
                  });
                }

                setShowSelectPrice(false);
              }}
            />
          ) : (
            <div className="flex flex-col gap-4">
              {(section?.prices as PlainMessage<Price>[] | undefined)?.map(
                (price, i, { length }) => (
                  <React.Fragment key={price.id}>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex flex-col">
                          <div className="flex flex-row gap-2">
                            {price.isUnlocked && <UnlockBadge type="price" />}
                            <span>{price.name}</span>
                          </div>
                          <span className="font-bold">
                            {moneyToCurrency(price.value)}
                          </span>
                        </div>

                        <TicketControls
                          series={seriesDetails.series}
                          eventOrBundle={eventOrBundle}
                          section={section as PlainMessage<Section>}
                          price={price}
                          availability={undefined}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {price.description}
                      </span>
                    </div>

                    {i !== length - 1 && <hr />}
                  </React.Fragment>
                ),
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              className="w-full text-link"
              onClick={() => setShowSelectPrice(false)}
            >
              {t.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeselectSeat} onOpenChange={setShowDeselectSeat}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deselect_seat.title}</DialogTitle>
            <DialogDescription className="flex flex-col">
              <span>
                {`${section?.name}, ${t.select_price.row} ${selectedSeat?.rowNumber},
                ${t.select_price.seat} ${selectedSeat?.seatName} ${
                  selectedSeat?.isAccessible ? t.select_price.accessible : ""
                }`}
              </span>
              <span className="text-black">{t.deselect_seat.description}</span>
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-end">
            <Button
              variant="outline"
              className="w-full text-link"
              onClick={() => {
                const seat = items.find((item) => {
                  return (
                    item.eventOrBundle.case === eventOrBundle.case &&
                    item.eventOrBundle.value.id === eventOrBundle.value?.id &&
                    item.section.id === section?.id &&
                    item.seat?.id === selectedSeat?.id
                  );
                });

                if (!seat) {
                  return;
                }

                cancelSeat(seat);
                setShowDeselectSeat(false);
              }}
            >
              {t.deselect_seat.deselect}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
